/**
 * Level math. Pure helpers — no React, no Supabase — so they're trivially
 * unit-testable and reused by the celebration logic + future stat displays.
 *
 * The rule: 500 XP per level. Level 1 starts at 0 XP, Level 2 at 500 XP,
 * Level N at (N-1) * 500 XP. Levels are unbounded (no soft cap) so a power
 * user grinding A1+A2 reaches ~Level 8-10 (rough Phase 12 budget = ~5000
 * XP for the entire course at first-try-perfect on every lesson).
 *
 * Why 500: Duolingo uses ~13 lessons/level (their math is opaque). A
 * fluent.ge perfect lesson = 25 XP, so 500 XP = 20 lessons = ~1.5 weeks at
 * a 1-lesson/day cadence. Felt right: rewarding enough that levels feel
 * like real milestones, not so cheap that they spam celebrations.
 */

export const XP_PER_LEVEL = 500;

/** The user's current level (1-indexed). */
export function xpToLevel(xp: number): number {
  if (xp <= 0) return 1;
  return Math.floor(xp / XP_PER_LEVEL) + 1;
}

/** XP threshold the user is currently working toward. */
export function nextLevelXp(xp: number): number {
  return xpToLevel(xp) * XP_PER_LEVEL;
}

/** XP earned within the current level (0..XP_PER_LEVEL-1). */
export function xpInLevel(xp: number): number {
  return xp - (xpToLevel(xp) - 1) * XP_PER_LEVEL;
}

/** 0..1 progress through the current level. */
export function levelProgress(xp: number): number {
  return xpInLevel(xp) / XP_PER_LEVEL;
}

/** True iff totalXpAfter pushes the user across a level boundary that
 *  totalXpBefore didn't. Used to fire the LevelUpModal exactly once per
 *  crossing. */
export function didLevelUp(totalXpBefore: number, totalXpAfter: number): boolean {
  return xpToLevel(totalXpAfter) > xpToLevel(totalXpBefore);
}

/** Streak milestones we celebrate. Sorted ascending. */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100, 365] as const;
export type StreakMilestone = (typeof STREAK_MILESTONES)[number];

/** If `streakAfter` JUST crossed a milestone (and `streakBefore` didn't have
 *  it yet), return that milestone — else null. Crossings happen at most one
 *  per lesson completion (streak only goes up by 1). */
export function streakMilestoneCrossed(
  streakBefore: number,
  streakAfter: number,
): StreakMilestone | null {
  if (streakAfter <= streakBefore) return null;
  for (const m of STREAK_MILESTONES) {
    if (streakBefore < m && streakAfter >= m) return m;
  }
  return null;
}
